import { max, min } from "@clockwise/client-commons/src/constants/time-slot";
import {
  FocusTimeThreshold,
  Maybe,
  MeetingReliefThreshold,
  PreferredLunchTimes,
  Scalars,
  SmartHold,
  SmartHoldState,
  TimeSlot,
} from "@clockwise/schema";
import { Duration } from "luxon";
import pluralize from "pluralize";

export const getLunchDurationConstraint = ({
  minDuration,
  idealDuration,
}: {
  minDuration: Scalars["Duration"];
  idealDuration: Scalars["Duration"];
}) => {
  const minDurationInMinutes = Duration.fromISO(minDuration).toFormat("m");
  const idealDurationInMinutes = Duration.fromISO(idealDuration).toFormat("m");
  return minDuration === idealDuration
    ? `${minDurationInMinutes} mins`
    : `${minDurationInMinutes} - ${idealDurationInMinutes} mins`;
};

export const getLunchTimeConstraint = ({
  minSlot,
  maxSlot,
}: {
  minSlot: TimeSlot;
  maxSlot: TimeSlot;
}) => {
  const minTime = min[minSlot];
  const maxTime = max[maxSlot];
  return `${minTime} - ${maxTime}`;
};

export const getReadableLunchGoals = ({
  preferredLunchTimes,
}: {
  preferredLunchTimes: Maybe<PreferredLunchTimes> | undefined;
}) => {
  if (!preferredLunchTimes) {
    return null;
  }

  const { minDuration, idealDuration, minSlot, maxSlot } = preferredLunchTimes;
  const durationConstraint = getLunchDurationConstraint({ minDuration, idealDuration });
  const timeConstraint = getLunchTimeConstraint({ minSlot, maxSlot });

  const goalDescription = `You asked Clockwise to schedule ${durationConstraint} for lunch between ${timeConstraint} when possible`;
  return goalDescription;
};

export const getReadableFocusTimeGoals = ({
  threshold,
}: {
  threshold: FocusTimeThreshold | null | undefined;
}) => {
  if (!threshold) {
    return null;
  }

  const idealFtHours =
    threshold.__typename === "SomeFocusTime"
      ? Duration.fromISO(threshold.idealFocus).as("hours")
      : null;

  const goalDescription =
    idealFtHours && threshold.__typename === "SomeFocusTime"
      ? `You asked Clockwise to schedule up to ${idealFtHours} ${pluralize(
          "hour",
          idealFtHours,
        )} of Focus Time per week`
      : "You asked Clockwise to schedule all your available hours";

  return goalDescription;
};

const parseThreshold = (threshold: MeetingReliefThreshold, variant: "short" | "long") => {
  const meetingReliefMins = Duration.fromISO(threshold.meetingReliefBreak).as("minutes");
  const meetingRelief = `${meetingReliefMins} min`;

  const fatigueThresholdHours = Duration.fromISO(threshold.fatigueThreshold).as("hours");
  const fatigueThresholdMinutes = Duration.fromISO(threshold.fatigueThreshold).as("minutes");
  let fatigueThreshold = "";
  if (variant === "short") {
    fatigueThreshold =
      fatigueThresholdHours < 1
        ? `${fatigueThresholdMinutes}+ mins`
        : `${fatigueThresholdHours}+ hrs`;
  } else {
    fatigueThreshold =
      fatigueThresholdHours < 1
        ? `${fatigueThresholdMinutes} mins or more`
        : `${fatigueThresholdHours} hrs or more`;
  }

  return { meetingRelief, fatigueThreshold };
};

const getShortMeetingReliefGoals = (threshold: MeetingReliefThreshold | null | undefined) => {
  if (!threshold) {
    return "Prevent continuous meetings";
  }

  const { meetingRelief, fatigueThreshold } = parseThreshold(threshold, "short");
  return `${meetingRelief} breaks • Prevent ${fatigueThreshold} of meetings`;
};

const getLongMeetingReliefGoals = (threshold: MeetingReliefThreshold | null | undefined) => {
  if (!threshold) {
    return "You asked Clockwise to schedule breaks to prevent continuous meetings";
  }

  const { meetingRelief, fatigueThreshold } = parseThreshold(threshold, "long");
  return `You asked Clockwise to schedule ${meetingRelief} breaks to prevent ${fatigueThreshold} of continuous meetings`;
};

export const getReadableMeetingReliefGoals = ({
  threshold,
  variant = "long",
}: {
  threshold: MeetingReliefThreshold | null | undefined;
  variant?: "short" | "long";
}) => {
  if (variant === "short") {
    return getShortMeetingReliefGoals(threshold);
  }

  return getLongMeetingReliefGoals(threshold);
};

export const GENERIC_SMART_HOLD_DISPLAY_NAME: Record<SmartHold["__typename"], string> = {
  FocusTimeHold: "Focus Time",
  LunchHold: "Lunch",
  MeetingReliefHold: "Meeting Break",
  TravelTimeHold: "Travel Time",
};

export const UNPAUSED_SMART_HOLD_DISPLAY_NAME: Record<SmartHold["__typename"], string> = {
  FocusTimeHold: "Automatic Focus Time",
  LunchHold: "Clockwise managed Lunch",
  MeetingReliefHold: "Automatic Meeting Break",
  TravelTimeHold: "Automatic Travel Time",
};

export const getSmartHoldMovementDescription = ({
  holdName,
  holdState,
}: {
  holdName: SmartHold["__typename"];
  holdState: SmartHoldState["__typename"] | undefined;
}) => {
  switch (holdState) {
    case "Paused":
      return `The timing of this ${GENERIC_SMART_HOLD_DISPLAY_NAME[holdName]} event is set, because it was edited.`;
    case "Protected":
      return `The timing of this ${GENERIC_SMART_HOLD_DISPLAY_NAME[holdName]} event is set, because it is being protected.`;
    case "Unpaused":
    default:
      return `The timing and duration of this ${GENERIC_SMART_HOLD_DISPLAY_NAME[holdName]} event may update as your schedule changes.`;
  }
};
